import {
  QueryClient,
  QueryCache,
  MutationCache,
  QueryClientProvider
} from '@tanstack/react-query';
import type { Mutation, Query } from '@tanstack/react-query';
import React from 'react';

import CustomError from './solaris-lens-common-lib/src/utils/CustomError';

export const GENERIC_GLOBAL_ERROR_MESSAGE =
  'The server was unable to process this request. Our team has been notified.';

export type GlobalErrorDisplay = 'card' | 'toast' | 'generic';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { errorDisplay?: GlobalErrorDisplay };
    mutationMeta: { errorDisplay?: GlobalErrorDisplay };
  }
}

const isGlobalHttpError = (error: unknown): error is CustomError =>
  (error as CustomError)?.isCustomError === true;

export type GlobalError = {
  error: CustomError;
  display: GlobalErrorDisplay;
} | null;

let globalError: GlobalError = null;
let listeners: ((error: GlobalError) => void)[] = [];

const activeGlobalErrorSources = new Set<string>();

const errorSourceIdFromQuery = (query: Pick<Query, 'queryHash'>) =>
  `query:${query.queryHash}`;

const errorSourceIdFromMutation = (
  mutation: Pick<Mutation<unknown, unknown, unknown, unknown>, 'mutationId'>
) => `mutation:${String(mutation.mutationId)}`;

const removeErrorSourceAndMaybeClearGlobal = (sourceId: string) => {
  activeGlobalErrorSources.delete(sourceId);
  if (activeGlobalErrorSources.size === 0 && globalError !== null) {
    globalError = null;
    listeners.forEach(l => l(globalError));
  }
};

export const getGlobalError = () => globalError;

export const clearGlobalError = () => {
  activeGlobalErrorSources.clear();
  if (globalError === null) {
    return;
  }
  globalError = null;
  listeners.forEach(l => l(globalError));
};

export const subscribeGlobalError = (cb: (error: GlobalError) => void) => {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter(l => l !== cb);
  };
};

export const setGlobalError = (
  error: unknown,
  display: GlobalErrorDisplay = 'generic'
) => {
  if (isGlobalHttpError(error)) {
    globalError = { error, display };
    listeners.forEach(l => l(globalError));
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      activeGlobalErrorSources.add(errorSourceIdFromQuery(query));
      const display: GlobalErrorDisplay =
        (query.meta?.errorDisplay as GlobalErrorDisplay) ?? 'generic';
      setGlobalError(error, display);
    }
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      activeGlobalErrorSources.add(errorSourceIdFromMutation(mutation));
      const display: GlobalErrorDisplay =
        (mutation.options.meta?.errorDisplay as GlobalErrorDisplay) ??
        'generic';
      setGlobalError(error, display);
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 0
    }
  }
});

queryClient.getQueryCache().subscribe(event => {
  if (event.type !== 'updated') {
    return;
  }
  const query = event.query;
  if (query.state.status !== 'success' || query.state.fetchStatus !== 'idle') {
    return;
  }
  removeErrorSourceAndMaybeClearGlobal(errorSourceIdFromQuery(query));
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type !== 'updated') {
    return;
  }
  const mutation = event.mutation;
  if (mutation.state.status !== 'success') {
    return;
  }
  removeErrorSourceAndMaybeClearGlobal(errorSourceIdFromMutation(mutation));
});

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
